/**
 * Smart Logger Utility
 * 
 * Behavior:
 * - Development: All logs visible
 * - Production: Only errors visible (unless debug mode or admin user)
 * - Debug Mode: Enable by setting localStorage.setItem('debug', 'true')
 * - Admin Users: All logs visible when logged in as admin
 */

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug'

class Logger {
  private isDevelopment: boolean
  private debugMode: boolean
  private isAdmin: boolean

  constructor() {
    this.isDevelopment = import.meta.env.DEV
    this.debugMode = this.checkDebugMode()
    this.isAdmin = false // Will be updated when user logs in
    
    // Listen for debug mode changes
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange.bind(this))
    }
  }

  private checkDebugMode(): boolean {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('debug') === 'true'
  }

  private handleStorageChange(e: StorageEvent) {
    if (e.key === 'debug') {
      this.debugMode = e.newValue === 'true'
      if (this.debugMode) {
        console.log('🐛 Debug mode enabled')
      }
    }
  }

  /**
   * Update admin status (call this after user login)
   */
  setAdminStatus(isAdmin: boolean) {
    this.isAdmin = isAdmin
    if (isAdmin && !this.isDevelopment) {
      console.log('👑 Admin logging enabled')
    }
  }

  /**
   * Check if logging should be enabled
   */
  private shouldLog(level: LogLevel): boolean {
    // Always show errors
    if (level === 'error') return true
    
    // Show all logs in development
    if (this.isDevelopment) return true
    
    // Show all logs for admin users
    if (this.isAdmin) return true
    
    // Show all logs in debug mode
    if (this.debugMode) return true
    
    // Otherwise, suppress in production
    return false
  }

  /**
   * Add prefix to log messages
   */
  private formatMessage(message: string, emoji?: string): string {
    const prefix = emoji || '📋'
    return `${prefix} ${message}`
  }

  /**
   * General log
   */
  log(message: string, ...args: any[]) {
    if (this.shouldLog('log')) {
      console.log(this.formatMessage(message), ...args)
    }
  }

  /**
   * Info log
   */
  info(message: string, ...args: any[]) {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage(message, 'ℹ️'), ...args)
    }
  }

  /**
   * Warning log
   */
  warn(message: string, ...args: any[]) {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage(message, '⚠️'), ...args)
    }
  }

  /**
   * Error log (always shown)
   */
  error(message: string, ...args: any[]) {
    console.error(this.formatMessage(message, '❌'), ...args)
  }

  /**
   * Debug log (uses console.log in dev, hidden in prod unless admin/debug mode)
   */
  debug(message: string, ...args: any[]) {
    if (this.shouldLog('debug')) {
      // Use console.log instead of console.debug to ensure visibility in all browsers
      console.log(this.formatMessage(message, '🔍'), ...args)
    }
  }

  /**
   * Group logs together
   */
  group(label: string, callback: () => void) {
    if (this.shouldLog('log')) {
      console.group(this.formatMessage(label))
      callback()
      console.groupEnd()
    }
  }

  /**
   * Collapsed group
   */
  groupCollapsed(label: string, callback: () => void) {
    if (this.shouldLog('log')) {
      console.groupCollapsed(this.formatMessage(label))
      callback()
      console.groupEnd()
    }
  }

  /**
   * Enable debug mode programmatically
   */
  enableDebug() {
    localStorage.setItem('debug', 'true')
    this.debugMode = true
    console.log('🐛 Debug mode enabled. Reload page to see all logs.')
  }

  /**
   * Disable debug mode
   */
  disableDebug() {
    localStorage.removeItem('debug')
    this.debugMode = false
    console.log('🐛 Debug mode disabled.')
  }

  /**
   * Check current status
   */
  getStatus() {
    return {
      isDevelopment: this.isDevelopment,
      debugMode: this.debugMode,
      isAdmin: this.isAdmin,
      loggingEnabled: this.shouldLog('log')
    }
  }
}

// Create singleton instance
export const logger = new Logger()

// Log initialization in development
if (import.meta.env.DEV) {
  console.log('🔍 Logger initialized in DEVELOPMENT mode - all logs visible')
}

// Expose debug controls to window for easy access in console
if (typeof window !== 'undefined') {
  (window as any).enableDebug = () => logger.enableDebug();
  (window as any).disableDebug = () => logger.disableDebug();
  (window as any).loggerStatus = () => {
    const status = logger.getStatus()
    console.table(status)
    return status
  }
}

// Export default
export default logger
